/**
 * @fileoverview Taxonomy Audit Log Page Component
 * 
 * Displays comprehensive audit trail of all changes made to taxonomy structures throughout
 * the application. Users can view who made what changes, when they were made, and what
 * specifically changed. Features advanced sorting, filtering, and pagination capabilities.
 * 
 * @module components/pages/auditlogtaxonomy
 * @component
 * 
 * Features:
 * - Displays all taxonomy modification history in tabular format
 * - Real-time search across user, action, changes, timestamp, and taxonomy name
 * - Multi-column sorting (clickable headers toggle sort direction)
 * - Pagination with configurable page size (50 rows per page)
 * - Color-coded action badges (renamed, created, deleted, updated, restructure)
 * - Back button for navigation
 * - Access control enforcement
 * - Loading spinner during data fetch
 */

import React, { useState, useEffect } from 'react';
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import { getFilenameFromURL, getUsername } from "../../utils/common_functions";
import { apiCall } from "../../utils/server_utils";
import CommonIcon from '../common/customicon';
import Message from "../common/message";
import { API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE } from "../../utils/config_mapping";
import PageLoader from '../common/pageloader';
import * as ReactDOM from 'react-dom';
import {trackError} from "../../utils/error_tracker"
import { Search, ChevronDown, ChevronRight, Plus, Edit3, Logs, Layers, ListChecks, Tag, SearchCheck, CalendarClock, User, CheckCircle2, ChevronUp, ArrowLeft } from 'lucide-react';
import {getAllTaxonomyAuditLogs} from "../../services/admin"
import $ from "jquery";
import { hasPageAccess,accessDenied } from "../../utils/page_access";
import HeaderLogo  from "../structure/headerlogo";

/**
 * Badge Color Mapping - Bootstrap CSS classes for action type badges
 * Maps taxonomy audit action types to visual styling:
 * - renamed (primary blue), created (success green), deleted (danger red)
 * - updated (warning yellow), restructure (info cyan)
 * @type {Object<string, string>}
 */
const typeColors = {
  renamed: "badge bg-primary",
  created: "badge bg-success",
  deleted: "badge bg-danger",
  updated: "badge bg-warning text-dark",
  restructure: "badge bg-info text-dark",
};

const PAGE_SIZE = 50;

export function TaxonomyAuditLogTable() {
  let pageName = "Taxonomy Management";
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true); // State to track loading
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc'); // desc for newest first

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page on sort
  };

  // Filter data
  const filtered = data.filter(
    (item) =>
      item.user.toLowerCase().includes(search.toLowerCase()) ||
      item.action.toLowerCase().includes(search.toLowerCase()) ||
      item.changes.some(change => change.toLowerCase().includes(search.toLowerCase())) ||
      item.timestamp.toLowerCase().includes(search.toLowerCase()) ||
      (item.taxonomyName || item.taxonomy_name || "").toLowerCase().includes(search.toLowerCase())
  );

  // Sort data
  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    switch (sortColumn) {
      case 'taxonomyName':
        aVal = (a.taxonomyName || a.taxonomy_name || '').toLowerCase();
        bVal = (b.taxonomyName || b.taxonomy_name || '').toLowerCase();
        break;
      case 'timestamp':
        aVal = a.timestamp;
        bVal = b.timestamp;
        break;
      case 'user':
        aVal = a.user.toLowerCase();
        bVal = b.user.toLowerCase();
        break;
      case 'version':
        aVal = a.version || 0;
        bVal = b.version || 0;
        break;
      case 'action':
        aVal = a.action.toLowerCase();
        bVal = b.action.toLowerCase();
        break;
      case 'changes':
        aVal = a.changes.join('; ').toLowerCase();
        bVal = b.changes.join('; ').toLowerCase();
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const startIndex = (page - 1) * PAGE_SIZE;
  const paginated = sorted.slice(startIndex, startIndex + PAGE_SIZE);
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);

      const fetchData = async () => {

        $(".page_pro_load").hide();
        setLoading(true); // Stop loading spinner
        try {
          const result = await getAllTaxonomyAuditLogs();
          if (result.code === API_SUCCESS_RESPONSE_CODE) {
           setData(result.data);
           
          } else {
            console.warn("Unexpected response status:", result.status);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
        finally {
           setLoading(false); // Stop loading spinner
       }
      };
   useEffect(() => {
        fetchData();
   }, []); // Empty dependency array ensures this runs only once
  


  return (

    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          <div id="table_height">
            <div className="flex-grow-1" id="row_heading">
              <div className="page-title"><Logs className="me-2" size={28} />

Taxonomy Audit Log</div>
              <p className="text-muted my-2 ff-secondary">
                View changes made to the taxonomy structure
              </p>
            </div>
            <div id="app_err"></div>
          </div>

{hasPageAccess(pageName, "view") === false ? accessDenied() : 
           loading ? (
                 <PageLoader />
            ) :  data.length > 0 ?
          <>  
      {/* Search */}
      <div className="mb-3">
        <span
          className="badge bg-primary me-2"
          style={{ cursor: 'pointer', fontSize: '14px', padding: '6px 12px' }}
          onClick={() => window.history.back()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') window.history.back(); }}
        >
          <ArrowLeft size={14} className="me-1" />
          Back
        </span>
      </div>
      
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search history..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              <th onClick={() => handleSort('taxonomyName')} style={{cursor: 'pointer'}}>
                Taxonomy Name {sortColumn === 'taxonomyName' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
              </th>
              <th onClick={() => handleSort('timestamp')} style={{cursor: 'pointer'}}>
                Date & Time {sortColumn === 'timestamp' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
              </th>
              <th onClick={() => handleSort('user')} style={{cursor: 'pointer'}}>
                Changed By {sortColumn === 'user' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
              </th>
              <th onClick={() => handleSort('version')} style={{cursor: 'pointer'}}>
                Version {sortColumn === 'version' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
              </th>
              <th onClick={() => handleSort('action')} style={{cursor: 'pointer'}}>
                Type {sortColumn === 'action' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
              </th>
              <th onClick={() => handleSort('changes')} style={{cursor: 'pointer'}}>
                Change Summary {sortColumn === 'changes' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length > 0 ? (
              paginated.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.taxonomyName || item.taxonomy_name}</td>
                  <td>{item.timestamp}</td>
                  <td>{item.user}</td>
                  <td>{item.version || ""}</td>
                  <td>
                    <span className={typeColors[item.action]}>{item.action.charAt(0).toUpperCase() + item.action.slice(1)}</span>
                  </td>
                  <td>{item.changes.join("; ")}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center text-muted">
                  No matching records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span>
            Page {page} of {totalPages}
          </span>
          <div>
            <button
              className="btn btn-primary btn-sm me-2"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
      </> : "No taxonomy audit log available."}


          </div>
        </div>
        
      </div>  
  );
}

export default TaxonomyAuditLogTable;
